import {BarDatum} from '@nivo/bar';
import {ELEMENT} from './elements/base/ElementTypes';
import {SlidesAnalyticsDTO} from './proptypes/slidesAnalytics';
import {IQuestionAnswerType} from './elements/base/questionElementBase';

export type Analytics = {
    calendarAnalytics: CalendarAnalytics;
    questionsTotalAnalytics: QuestionsTotalAnalytics;
    userInteractions: UserInteractionsAnalytics;
    questionReport: QuestionReport[];
    textInputs: TextInputReport[];
    slideClicks: SlideClicksAnalytics[];
    leaderboard: LiveRoomReportDTO;
};

export type TextInputReport = {
    elementId: string;
    label: string;
    answers: string[];
};

export type SlideClicksAnalytics = {
    _id: string;
    slideId: string;
    clicks: Record<string, Record<string, number>>;
};

export type SlidesTopTimeAnalyticsDTO = Record<string, number>;

export type FormattedSlidesTopTimeAnalytics = {
    id: string;
    name: string;
    value: number;
}[];

export type FormattedClicksAnalytics = {
    sum: number;
    data: {id: string; name: string; value: number; label?: string | number}[];
};

export type FormattedViewsAnalytics = {
    sum: number;
    data: {id: string; name: string; value: number; label?: string | number}[];
};

export type SlidesAnalytics = {
    content: SlidesAnalyticsDTO[];
    pageSize: number;
    pageNumber: number;
    numberOfElements: number;
};

export type UserInteractionsAnalytics = {
    // number of accesses
    total: number;

    // number of accesses with no interactions
    noInteracts: number;

    // number of current active users
    activeUsers: number;

    // average time spent in presentation
    averageSessionTime: string;
};

export type QuestionsTotalAnalytics = {
    averageScore: number;
    maxPossibleScore: number;
    percentageOfMaxPossibleScore: number;
    maxScore: number;
    numOfReports: number;
};

export type CalendarAnalytics = {
    // daily access values
    dailyAccesses: {value: number; day: string}[];
};

export interface IMatchPairsReport {
    leftLabels: string[];
    rightLabels: string[];
    labelMatrix: Record<string, Record<string, {count: number; correct: boolean}>>;
    totals: Record<string, number>;
}

export type QuestionReport = {
    elementId: string;
    question: string;
    answers: QuestionReportAnswer[];
    slide: {id: string; name: string};
    totalAnswers: number;
    type: ELEMENT;
    isDebatable?: boolean;
    sequenceQuestionData?: {
        barPlotData?: BarDatum[];
        boxPlotData?: BarDatum[];
        rankDistributionData?: BarDatum[];
        rankDistributionKeys?: string[];
    };
    likertScaleData?: BarDatum[];
    maximumPoints?: number;
    averagePoints?: number;
    userAnsweredCount?: number;
    matchPairsReport?: IMatchPairsReport;
    imageMarkupPoints?: {x: number; y: number; value: number}[];
};

export type QuestionReportAnswer = {
    answerText: string;
    noOfAnswers: number;
    percentage: number;
    type: IQuestionAnswerType | undefined;
    rankingScore?: number;
    averageRanking?: number;
    rankingPercentages?: Record<string, number>;
};

export type QuestionAnswersCount = {
    elementId: string;
    count: number;
};

export type LeaderboardEntry = {
    id: string;
    rank?: number;
    name: string;
    score: number;
    isCurrentUser?: boolean;
};

export type LiveRoomReportDTO = {
    specificLeaderboard?: LeaderboardEntry[];
    groupLeaderboard?: LeaderboardEntry[];
    personalSpecificEntry?: LeaderboardEntry;
    personalGroupEntry?: LeaderboardEntry;
    questionReport?: QuestionReport[];
};

export enum QuestionAnswerEnum {
    CORRECT = 'correct',
    INCORRECT = 'incorrect',
    DEBATABLE = 'debatable',
    ALL_CORRECT = 'allCorrect',
    NEUTRAL = 'neutral',
}

export type QuestionAnswerType =
    | QuestionAnswerEnum.CORRECT
    | QuestionAnswerEnum.INCORRECT
    | QuestionAnswerEnum.DEBATABLE
    | QuestionAnswerEnum.ALL_CORRECT;

export type leaderboardTypes = keyof Pick<LiveRoomReportDTO, 'specificLeaderboard' | 'groupLeaderboard'>;
